/**
 * The heads-up display.
 *
 * Five readouts, each earning its pixels:
 *
 * - **altitude**, big, top centre — the only number that matters, with your
 *   personal best dimmed underneath it as a standing accusation;
 * - **progress bar**, left edge, 0 → summit, with a gold notch at your best;
 * - **charge**, bottom centre, gold and pulsing when it is full;
 * - **connection**, top right — player count and a dot you can read at a glance;
 * - **nearby climbers**, top left — the three ghosts closest to your altitude.
 *   That last one is the social pressure the whole genre runs on.
 *
 * `update` is called every rendered frame, so it does no DOM work it can avoid:
 * every field is compared against the last value written and the node is touched
 * only when it actually changed. No `innerHTML`, no allocation in the common case.
 */

import './ui.css';
import type { NetStatus } from '../net/client';

/** How many nearby climbers the leaderboard shows. */
const BOARD_ROWS = 3;

export interface HudRosterEntry {
  name: string;
  color: number;
  heightM: number;
}

export interface HudData {
  /** Current altitude in metres. */
  heightM: number;
  /** Personal best altitude in metres. */
  bestM: number;
  /** Summit altitude, for scaling the progress bar. */
  topY: number;
  /** Charge in `[0, 1]`. */
  charge: number;
  /** True when the charge is capped — drives the gold pulse. */
  chargeFull: boolean;
  /** Players online, including you. */
  players: number;
  status: NetStatus;
  /** Every known ghost with an altitude. Order does not matter. */
  roster: readonly HudRosterEntry[];
}

interface BoardRow {
  el: HTMLElement;
  swatch: HTMLElement;
  name: HTMLElement;
  height: HTMLElement;
  key: string;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  parent?: HTMLElement,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  parent?.appendChild(node);
  return node;
}

function hex(color: number): string {
  return `#${(color >>> 0).toString(16).padStart(6, '0').slice(-6)}`;
}

/** Status → dot modifier. `connecting` and `reconnecting` read identically to the player. */
function dotClass(status: NetStatus): string {
  switch (status) {
    case 'connected':
      return 'is-connected';
    case 'connecting':
    case 'reconnecting':
      return 'is-connecting';
    default:
      return 'is-offline';
  }
}

export class Hud {
  private readonly root: HTMLElement;
  private readonly heightText: Text;
  private readonly bestEl: HTMLElement;
  private readonly progFill: HTMLElement;
  private readonly progNotch: HTMLElement;
  private readonly chargeEl: HTMLElement;
  private readonly chargeFill: HTMLElement;
  private readonly connText: HTMLElement;
  private readonly connDot: HTMLElement;
  private readonly boardEl: HTMLElement;
  private readonly rows: BoardRow[] = [];

  // Last values written to the DOM. `NaN`/sentinels force the first write.
  private lastHeight = NaN;
  private lastBest = NaN;
  private lastFill = NaN;
  private lastNotch = NaN;
  private lastCharge = NaN;
  private lastChargeFull: boolean | null = null;
  private lastChargeActive: boolean | null = null;
  private lastPlayers = NaN;
  private lastStatus: NetStatus | null = null;
  private lastBoardCount = -1;

  /** Scratch array reused by the leaderboard sort — `update` allocates nothing. */
  private readonly near: HudRosterEntry[] = [];

  constructor() {
    const mount = document.getElementById('ui-root') ?? document.body;

    this.root = el('div', 'sk-hud', mount);
    this.root.hidden = true;

    // --- altitude ----------------------------------------------------------
    const alt = el('div', 'sk-alt', this.root);
    const height = el('div', 'sk-alt__height', alt);
    this.heightText = document.createTextNode('0');
    height.appendChild(this.heightText);
    el('span', undefined, height).textContent = 'm';
    this.bestEl = el('div', 'sk-alt__best', alt);

    // --- progress ----------------------------------------------------------
    const prog = el('div', 'sk-prog', this.root);
    this.progFill = el('div', 'sk-prog__fill', prog);
    this.progNotch = el('div', 'sk-prog__notch', prog);

    // --- charge ------------------------------------------------------------
    this.chargeEl = el('div', 'sk-charge', this.root);
    this.chargeFill = el('div', 'sk-charge__fill', this.chargeEl);

    // --- connection --------------------------------------------------------
    const conn = el('div', 'sk-conn', this.root);
    this.connText = el('span', 'sk-num', conn);
    this.connDot = el('span', 'sk-conn__dot', conn);

    // --- nearby climbers ---------------------------------------------------
    this.boardEl = el('div', 'sk-board', this.root);
    el('div', 'sk-board__title', this.boardEl).textContent = 'NEARBY';
    for (let i = 0; i < BOARD_ROWS; i++) {
      const row = el('div', 'sk-board__row', this.boardEl);
      row.hidden = true;
      this.rows.push({
        el: row,
        swatch: el('span', 'sk-board__swatch', row),
        name: el('span', 'sk-board__name', row),
        height: el('span', 'sk-board__h sk-num', row),
        key: '',
      });
    }
    this.boardEl.hidden = true;
  }

  show(): void {
    this.root.hidden = false;
  }

  hide(): void {
    this.root.hidden = true;
  }

  /** Release the overlay. The instance is dead afterwards. */
  dispose(): void {
    this.root.remove();
  }

  update(d: HudData): void {
    this.updateAltitude(d);
    this.updateProgress(d);
    this.updateCharge(d);
    this.updateConnection(d);
    this.updateBoard(d);
  }

  // -------------------------------------------------------------------------

  private updateAltitude(d: HudData): void {
    const h = Math.round(d.heightM);
    if (h !== this.lastHeight) {
      this.lastHeight = h;
      this.heightText.nodeValue = String(h);
    }
    const best = Math.round(d.bestM);
    if (best !== this.lastBest) {
      this.lastBest = best;
      // Before there is a best worth naming, say nothing rather than `BEST 0 m`.
      this.bestEl.textContent = best > 0 ? `BEST ${best} m` : '';
    }
  }

  private updateProgress(d: HudData): void {
    const span = d.topY > 0 ? d.topY : 1;
    const fill = Math.round(clamp01(d.heightM / span) * 1000) / 10;
    if (fill !== this.lastFill) {
      this.lastFill = fill;
      this.progFill.style.height = `${fill}%`;
    }
    const notch = Math.round(clamp01(d.bestM / span) * 1000) / 10;
    if (notch !== this.lastNotch) {
      this.lastNotch = notch;
      this.progNotch.style.bottom = `${notch}%`;
      // A notch at the floor is noise, not a personal best.
      this.progNotch.style.display = d.bestM > 0.5 ? '' : 'none';
    }
  }

  private updateCharge(d: HudData): void {
    const pct = Math.round(clamp01(d.charge) * 200) / 2; // 0.5% granularity
    if (pct !== this.lastCharge) {
      this.lastCharge = pct;
      this.chargeFill.style.width = `${pct}%`;
    }
    const active = d.charge > 0.005;
    if (active !== this.lastChargeActive) {
      this.lastChargeActive = active;
      this.chargeEl.classList.toggle('is-active', active);
    }
    if (d.chargeFull !== this.lastChargeFull) {
      this.lastChargeFull = d.chargeFull;
      this.chargeEl.classList.toggle('is-full', d.chargeFull);
    }
  }

  private updateConnection(d: HudData): void {
    if (d.players !== this.lastPlayers) {
      this.lastPlayers = d.players;
      this.connText.textContent = `${d.players} ONLINE`;
    }
    if (d.status !== this.lastStatus) {
      this.lastStatus = d.status;
      this.connDot.className = `sk-conn__dot ${dotClass(d.status)}`;
    }
  }

  /**
   * The three climbers nearest your altitude — above or below, whichever is
   * closer — listed highest first. Someone 4 m over your head is far more
   * interesting than the leader 90 m up, so this sorts by |Δaltitude| and only
   * then presents the survivors in descending order.
   */
  private updateBoard(d: HudData): void {
    const near = this.near;
    near.length = 0;
    for (const e of d.roster) near.push(e);

    const mine = d.heightM;
    near.sort((a, b) => Math.abs(a.heightM - mine) - Math.abs(b.heightM - mine));
    if (near.length > BOARD_ROWS) near.length = BOARD_ROWS;
    near.sort((a, b) => b.heightM - a.heightM);

    if (near.length !== this.lastBoardCount) {
      this.lastBoardCount = near.length;
      this.boardEl.hidden = near.length === 0;
    }

    for (let i = 0; i < this.rows.length; i++) {
      const row = this.rows[i];
      if (row === undefined) continue;
      const entry = near[i];
      if (entry === undefined) {
        if (!row.el.hidden) {
          row.el.hidden = true;
          row.key = '';
        }
        continue;
      }
      const h = Math.round(entry.heightM);
      const key = `${entry.name}|${entry.color}|${h}`;
      if (key === row.key) {
        if (row.el.hidden) row.el.hidden = false;
        continue;
      }
      row.key = key;
      row.el.hidden = false;
      row.swatch.style.background = hex(entry.color);
      row.name.textContent = entry.name;
      row.height.textContent = `${h} m`;
    }
  }
}

function clamp01(v: number): number {
  return Number.isFinite(v) ? (v < 0 ? 0 : v > 1 ? 1 : v) : 0;
}
